import db from "../db/database";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import { isOnline } from "./syncService";
import { anotarError } from "./syncLog";

/**
 * Productos adoptados por cada colegio y grado. Sólo Perú.
 *
 * Lee el servicio OData `ProductosAdoptados`: una fila por (colegio, grado,
 * producto). Es lo que usa la venta para cargar de una vez la lista escolar
 * al elegir Colegio y Grado --ver `productosAdoptadosDe` y `InvoiceLines`--.
 *
 * Se lee entera en cada sincronización completa y se reemplaza la tabla: el
 * servicio no trae fecha de modificación con la que leer sólo lo nuevo, y las
 * bajas de adopciones también tienen que desaparecer del terminal.
 */

/** El nombre de la tabla en Dexie y de la entidad en `syncMetadata`. */
export const ENTIDAD = "productosAdoptados";

/** El servicio web de Business Central. */
export const SERVICIO = "ProductosAdoptados";

const texto = (valor) => String(valor ?? "").trim();

// Los campos se mapean uno por uno, sin spread: si BC renombra uno, que falte a
// la vista y no que se cuele con otro nombre.
export const mapProductoAdoptadoFromBC = (fila) => ({
  Contact_No: texto(fila?.Contact_No),
  Contact_Name: texto(fila?.Contact_Name),
  Grade: texto(fila?.Grade),
  Item_No: texto(fila?.Item_No),
  Item_Description: texto(fila?.Item_Description),
  lastSynced: new Date(),
});

// Sin colegio, grado o producto la fila no sirve para cargar nada.
const esFilaUtil = (fila) => Boolean(fila.Contact_No && fila.Grade && fila.Item_No);

export const syncProductosAdoptados = async () => {
  if (!isOnline()) {
    return false;
  }

  try {
    const leidas = await fetchFromBusinessCentral(SERVICIO);

    const filas = (Array.isArray(leidas) ? leidas : [])
      .map(mapProductoAdoptadoFromBC)
      .filter(esFilaUtil);

    // Se reemplaza la tabla sólo después de leer bien: un fallo de red a mitad
    // no debe dejar al terminal sin las listas escolares.
    await db.transaction("rw", db.productosAdoptados, async () => {
      await db.productosAdoptados.clear();
      if (filas.length) {
        await db.productosAdoptados.bulkAdd(filas);
      }
    });

    return true;
  } catch (error) {
    anotarError(ENTIDAD, "falló la lectura", error);
    return false;
  }
};

/**
 * Los grados que tienen productos adoptados en un colegio, ordenados.
 *
 * @param {string} colegioNo - El `No` del colegio (el `Contact_No` de BC).
 * @returns {Promise<string[]>}
 */
export const gradosDelColegio = async (colegioNo) => {
  const colegio = texto(colegioNo);
  if (!colegio) return [];

  const filas = await db.productosAdoptados.where("Contact_No").equals(colegio).toArray();
  return [...new Set(filas.map((fila) => fila.Grade))].sort((a, b) =>
    a.localeCompare(b, "es", { numeric: true })
  );
};

/**
 * Los `Item_No` adoptados por un colegio en un grado, sin repetir y en el orden
 * en que los manda BC.
 *
 * @param {string} colegioNo
 * @param {string} grado
 * @returns {Promise<string[]>}
 */
export const productosAdoptadosDe = async (colegioNo, grado) => {
  const colegio = texto(colegioNo);
  const elGrado = texto(grado);
  if (!colegio || !elGrado) return [];

  const filas = await db.productosAdoptados
    .where("[Contact_No+Grade]")
    .equals([colegio, elGrado])
    .toArray();
  return [...new Set(filas.map((fila) => fila.Item_No))];
};

/**
 * La descripción con la que BC publica cada producto adoptado de un grado.
 *
 * Sirve para nombrar en los avisos los productos que **no** están en el
 * catálogo de la tienda: de ésos el terminal no tiene ficha, y sin esto sólo se
 * podría enseñar el código pelado.
 *
 * @param {string} colegioNo
 * @param {string} grado
 * @returns {Promise<Map<string, string>>} Item_No → descripción.
 */
export const descripcionesDeAdoptados = async (colegioNo, grado) => {
  const colegio = texto(colegioNo);
  const elGrado = texto(grado);
  if (!colegio || !elGrado) return new Map();

  const filas = await db.productosAdoptados
    .where("[Contact_No+Grade]")
    .equals([colegio, elGrado])
    .toArray();
  return new Map(filas.map((fila) => [fila.Item_No, fila.Item_Description]));
};
